// client.service.ts
import { Injectable, inject } from '@angular/core';
import {
  Firestore,
  collection,
  doc,
  addDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
} from '@angular/fire/firestore';
import { Client } from '../models/client';
import { from, map, switchMap } from 'rxjs';
import { query, where } from '@angular/fire/firestore';

@Injectable({
  providedIn: 'root',
})
export class ClientService {
  private firestore = inject(Firestore);
  private clientsCol = collection(this.firestore, 'clients');

  /** Create a new client */
  createClient(client: Client) {
    return from(addDoc(this.clientsCol, client.toFirestore())).pipe(
      map((docRef) => ({ ...client, client_id: docRef.id }))
    );
  }

  /** Get all clients (one-time) */
  getClients() {
    return from(getDocs(this.clientsCol)).pipe(
      map((snapshot) =>
        snapshot.docs.map((d) => Client.fromFirestore(d.id, d.data()))
      )
    );
  }

  /** Get a single client by ID */
  getClientById(id: string) {
    const ref = doc(this.firestore, 'clients', id);
    return from(getDoc(ref)).pipe(
      map((snapshot) => {
        if (!snapshot.exists()) throw new Error('Client not found');
        return Client.fromFirestore(snapshot.id, snapshot.data());
      })
    );
  }
  /** Get a single client by User ID */
  getClientByUserId(user_id: string) {
    const q = query(this.clientsCol, where('user_id', '==', user_id));
    return from(getDocs(q)).pipe(
      map((snapshot) => {
        if (snapshot.empty) return null;
        const doc = snapshot.docs[0];
        return Client.fromFirestore(doc.id, doc.data());
      })
    );
  }

  /** Update a client */
  updateClient(client: Client) {
    const ref = doc(this.firestore, 'clients', client.client_id);
    return from(updateDoc(ref, client.toFirestore())).pipe(map(() => client));
  }

  /** Delete a client */
  deleteClient(id: string) {
    const ref = doc(this.firestore, 'clients', id);
    return from(deleteDoc(ref)).pipe(map(() => id));
  }
}
